// src/components/reports/EnhancedReportCard.jsx
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { Download, Eye, Filter, BarChart3, PieChart, TrendingUp, AlertCircle } from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import ReportSpecificFilters from './ReportSpecificFilters'
import reportsService from '../../services/reportsService'

const MiniChart = ({ data, type = 'bar' }) => {
  if (!data || data.length === 0) return null

  const maxValue = Math.max(...data.slice(0, 5).map(item => parseFloat(item.value || item.totalAmount || item.totalRevenue || 0)))

  return (
    <div className="mt-4">
      <h4 className="text-sm font-medium text-gray-600 mb-2">Quick View</h4>
      <div className="space-y-2">
        {data.slice(0, 5).map((item, index) => {
          const value = parseFloat(item.value || item.totalAmount || item.totalRevenue || item.stockLevel || 0)
          const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0
          
          return (
            <div key={index} className="flex items-center space-x-2">
              <div className="w-24 text-xs text-gray-600 truncate">
                {item.name || item.paymentMethod || item.productName || item.customerName || item.date || `Item ${index + 1}`}
              </div>
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <div className="text-xs font-medium text-gray-900 w-16 text-right">
                {typeof value === 'number' ? (
                  value > 1000 ? `Rs ${(value/1000).toFixed(1)}k` : `Rs ${value.toFixed(0)}`
                ) : value}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const SummaryStats = ({ summary, reportType }) => {
  const getStatsForType = () => {
    switch (reportType) {
      case 'sales-summary':
        return [
          { label: 'Total Revenue', value: `Rs ${summary.totalRevenue?.toLocaleString() || 0}`, color: 'text-green-600' },
          { label: 'Total Orders', value: summary.totalOrders?.toLocaleString() || 0, color: 'text-blue-600' },
          { label: 'Avg Order', value: `Rs ${summary.avgOrderValue?.toFixed(0) || 0}`, color: 'text-purple-600' },
          { label: 'Total Discount', value: `Rs ${summary.totalDiscount?.toLocaleString() || 0}`, color: 'text-red-600' }
        ]
      case 'payments':
        return [
          { label: 'Total Amount', value: `Rs ${summary.totalAmount?.toLocaleString() || 0}`, color: 'text-green-600' },
          { label: 'Transactions', value: summary.totalTransactions?.toLocaleString() || 0, color: 'text-blue-600' },
          { label: 'Payment Methods', value: summary.methodsCount || 0, color: 'text-purple-600' },
          { label: 'Avg Transaction', value: `Rs ${summary.avgTransactionValue?.toFixed(0) || 0}`, color: 'text-orange-600' }
        ]
      case 'product-summary':
        return [
          { label: 'Total Products', value: summary.totalProducts || 0, color: 'text-blue-600' },
          { label: 'In Stock', value: summary.inStock || 0, color: 'text-green-600' },
          { label: 'Low Stock', value: summary.lowStock || 0, color: 'text-red-600' },
          { label: 'Total Value', value: `Rs ${summary.totalValue?.toLocaleString() || 0}`, color: 'text-purple-600' }
        ]
      case 'stock-alerts':
        return [
          { label: 'Total Alerts', value: summary.totalAlerts || 0, color: 'text-red-600' },
          { label: 'Out of Stock', value: summary.outOfStock || 0, color: 'text-red-700' },
          { label: 'Low Stock', value: summary.lowStock || 0, color: 'text-yellow-600' },
          { label: 'Stock Value', value: `Rs ${summary.totalValue?.toLocaleString() || 0}`, color: 'text-blue-600' }
        ]
      case 'customers':
        return [
          { label: 'Total Customers', value: summary.totalCustomers || 0, color: 'text-blue-600' },
          { label: 'Active', value: summary.activeCustomers || 0, color: 'text-green-600' },
          { label: 'Loyalty Members', value: summary.loyaltyMembers || 0, color: 'text-purple-600' },
          { label: 'Avg Loyalty Points', value: summary.avgLoyaltyPoints?.toFixed(0) || 0, color: 'text-orange-600' }
        ]
      default:
        return []
    }
  }

  const stats = getStatsForType()

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      {stats.map((stat, index) => (
        <div key={index} className="text-center p-3 bg-gray-50 rounded-lg">
          <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
          <div className="text-xs text-gray-600">{stat.label}</div>
        </div>
      ))}
    </div>
  )
}

const getReportIcon = (reportType) => {
  switch (reportType) {
    case 'sales-summary': return TrendingUp
    case 'payments': return PieChart
    case 'product-summary': return BarChart3
    case 'stock-alerts': return AlertCircle
    default: return BarChart3
  }
}

export const EnhancedReportCard = ({ 
  title, 
  data: initialData, 
  columns, 
  filters, 
  onFilterChange,
  onExport,
  onViewDetails,
  summary,
  loading: externalLoading = false,
  reportType = 'default'
}) => {
  const [showFilters, setShowFilters] = useState(false)
  const [localFilters, setLocalFilters] = useState(filters || {})
  const [viewMode, setViewMode] = useState('summary') // summary, table, chart
  const [reportData, setReportData] = useState(initialData || [])
  const [reportSummary, setReportSummary] = useState(summary)
  const [internalLoading, setInternalLoading] = useState(false)

  // Use internal loading if we're doing our own data fetching, otherwise use external loading
  const loading = internalLoading || externalLoading

  // Define loadReportData first (before useEffect that uses it)
  const loadReportData = useCallback(async (filters = {}) => {
    if (!reportType || reportType === 'default') {
      console.log('⚠️ Skipping loadReportData - invalid reportType:', reportType)
      return
    }

    console.log('🔄 Starting loadReportData:', { reportType, filters, hasFilters: Object.keys(filters).length > 0 })
    setInternalLoading(true)
    try {
      console.log('📞 Calling reportsService.generateReport...')
      const result = await reportsService.generateReport(reportType, filters)
      console.log('📥 Received result:', { 
        dataLength: result?.data?.length || 0, 
        hasSummary: !!result?.summary,
        result: result
      })
      setReportData(Array.isArray(result?.data) ? result.data : [])
      setReportSummary(result?.summary)
      console.log('✅ Report data updated successfully')
    } catch (error) {
      console.error('❌ Error loading report data:', error)
      setReportData([])
      setReportSummary(null)
    } finally {
      setInternalLoading(false)
    }
  }, [reportType])

  // Initialize data when initialData changes
  useEffect(() => {
    if (initialData) {
      setReportData(Array.isArray(initialData) ? initialData : [])
    } else if (reportType && reportType !== 'default') {
      // Load initial data if no initial data is provided
      console.log('🚀 Loading initial report data for:', reportType)
      loadReportData({})
    }
  }, [initialData, reportType, loadReportData])

  // Initialize summary when it changes
  useEffect(() => {
    if (summary) {
      setReportSummary(summary)
    }
  }, [summary])

  const handleFilterChange = async (newFilters) => {
    console.log('🎯 Filter change:', { reportType, newFilters })
    setLocalFilters(newFilters)
    
    // Load new data with the filters
    await loadReportData(newFilters)
    
    // Also notify parent component if needed
    onFilterChange?.(newFilters)
  }

  const handleFilterApply = () => {
    setShowFilters(false)
  }

  const handleFilterClear = async () => {
    const emptyFilters = {}
    setLocalFilters(emptyFilters)
    await loadReportData(emptyFilters)
    onFilterChange?.(emptyFilters)
    setShowFilters(false)
  }

  // Ensure data is always an array
  const safeData = Array.isArray(reportData) ? reportData : []

  const handleExport = (format) => {
    onExport?.(safeData, title, format)
  }

  const ReportIcon = getReportIcon(reportType)

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ReportIcon className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
            <span className="text-sm text-gray-500">({safeData.length} records)</span>
          </div>
          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('summary')}
                className={`px-2 py-1 text-xs rounded ${viewMode === 'summary' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
              >
                Summary
              </button>
              <button
                onClick={() => setViewMode('chart')}
                className={`px-2 py-1 text-xs rounded ${viewMode === 'chart' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
              >
                Chart
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-2 py-1 text-xs rounded ${viewMode === 'table' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
              >
                Table
              </button>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-1"
            >
              <Filter className="h-3 w-3" />
              <span>Filter</span>
            </Button>
            
            <div className="relative group">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center space-x-1"
              >
                <Download className="h-3 w-3" />
                <span>Export</span>
              </Button>
              
              <div className="absolute right-0 mt-2 w-32 bg-white border rounded-lg shadow-lg z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-1">
                  <button
                    onClick={() => handleExport('csv')}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    PDF
                  </button>
                  <button
                    onClick={() => handleExport('excel')}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Excel
                  </button>
                </div>
              </div>
            </div>
            
            {onViewDetails && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetails(safeData)}
                className="flex items-center space-x-1"
              >
                <Eye className="h-3 w-3" />
                <span>Details</span>
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Report-Specific Filter Panel */}
        {showFilters && (
          <div className="mb-4">
            <ReportSpecificFilters
              reportType={reportType}
              filters={localFilters}
              onFilterChange={handleFilterChange}
              onApply={handleFilterApply}
              onClear={handleFilterClear}
            />
          </div>
        )}

        {/* Content based on view mode */}
        {viewMode === 'summary' && (
          <>
            {reportSummary && <SummaryStats summary={reportSummary} reportType={reportType} />}
            <MiniChart data={safeData} />
          </>
        )}

        {viewMode === 'chart' && (
          <div className="h-64 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>Chart visualization coming soon!</p>
            </div>
          </div>
        )}

        {viewMode === 'table' && (
          <div className="overflow-x-auto">
            {safeData.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns?.map((column, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.header || column.key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {safeData.slice(0, 10).map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50">
                      {columns?.map((column, colIndex) => (
                        <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {(() => {
                            const value = row[column.key]
                            if (value === null || value === undefined) return 'N/A'
                            if (typeof value === 'object') {
                              // Handle object values - try to get name, title, or convert to string
                              if (value.name) return value.name
                              if (value.title) return value.title
                              return JSON.stringify(value)
                            }
                            return value
                          })()}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No data available
              </div>
            )}
            {safeData.length > 10 && (
              <div className="text-center py-2 text-sm text-gray-500">
                Showing first 10 of {safeData.length} records
              </div>
            )}
          </div>
        )}

        {/* No data state */}
        {safeData.length === 0 && viewMode !== 'chart' && (
          <div className="text-center py-12 text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium">No data available</p>
            <p className="text-sm">Try adjusting your filters or check back later</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default EnhancedReportCard